import { Grid, Typography, Button, Fab, Modal, TextField } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { Group, useCreateGroup, useListGroups } from "../hooks/useAdmin";
import { useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import { ConfirmModal } from "./Modals/ConfirmModal";
import { BACKEND_URL } from "../constants";


enum AdminGroupsModalState {
  GROUP_ADD,
  CONFIRM_DELETE,
};

export const AdminGroups = () => {
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const { data: groups, isLoading: groupsLoading, refetch: refetchGroups } = useListGroups();
  const { post: createGroup, isLoading: createGroupLoading } = useCreateGroup();
  const [modalState, setModalState] = useState<AdminGroupsModalState | undefined>();
  const [groupName, setGroupName] = useState<string>('');

  return (
    <Grid size={11} spacing={2} container>
      <ConfirmModal
        open={modalState === AdminGroupsModalState.CONFIRM_DELETE}
        onConfirm={async () => {
          // we call delete on this group
          try {
            // Call your backend endpoint
            const token = localStorage.getItem("jwt");
            const response = await fetch(`${BACKEND_URL}/admin/group/${selectedGroup?.id}`, {
              method: "DELETE",
              headers: {
                Authorization: token ?? "",
              },
            });

            if (!response.ok) {
              throw new Error("Failed to delete group");
            }

            await refetchGroups();
            setModalState(undefined);
          } catch (err) {
            console.error(err);
          }
        }}
        onCancel={() => {
          setModalState(undefined);
        }}
        title={`Delete Group '${selectedGroup?.name}'`}
        message={`Are you sure you want to delete this group?
           This will delete all scenes & assets associated with the group as well!`}
      />
      <Fab
        sx={(theme) => ({
          backgroundColor: theme.palette.action.active,
          position: "fixed",
          bottom: 16,  // distance from bottom
          right: 16,   // distance from right
          zIndex: 1000, // keep above most content
        })}
        onClick={() => {
          setModalState(AdminGroupsModalState.GROUP_ADD);
        }}
      >
        <AddIcon />
      </Fab>
      <Grid size={12}>
        <DataGrid
          loading={groupsLoading}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row--selected-custom': {
              backgroundColor: 'action.selected',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            selectedGroup?.id === params.row.id ? 'MuiDataGrid-row--selected-custom' : ''
          }
          onRowClick={(params, e) => {
            e.preventDefault();
            // set this as the selected user
            setSelectedGroup(params.row);
          }}
          columns={[
            {
              field: 'ID',
              valueGetter: (_, row) => {
                return row.id;
              },
              flex: 1,
            },
            {
              field: 'Name',
              valueGetter: (_, row) => {
                return row.name;
              },
              flex: 1,
            },
            {
              field: 'Created at',
              valueGetter: (_, row) => {
                return new Date(row.createdAt).toString();
              },
              flex: 1,
            },
            {
              field: 'Number of Members',
              valueGetter: (_, row) => {
                return row.users.length;
              },
              flex: 1,
            },
          ]}
          rows={groups ?? []}
        />
      </Grid>
      {selectedGroup && (
        <Grid container spacing={2} size={12}>
          <Grid size={12}>
            <Typography color="primary">Actions</Typography>
          </Grid>
          <Grid size={12}>
            <Grid size={12} spacing={2} container flexDirection='row'>
              {selectedGroup.users.length === 0 && !["ADMIN", "PENDING"].includes(selectedGroup.name) && (
                <Grid size={1}>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={() => {
                      setModalState(AdminGroupsModalState.CONFIRM_DELETE);
                    }}
                  >
                    Delete
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={12}>
          </Grid>
        </Grid>
      )}
      {/* Add modal */}
      <Modal
        open={modalState === AdminGroupsModalState.GROUP_ADD}
        onClose={() => {
          setModalState(undefined);
          setGroupName('');
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Grid
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            padding: 3,
            minWidth: 400,
            boxShadow: 24,
            outline: "none",
            display: "flex",
            flexDirection: "row",   // stack vertically
            gap: 4,                 // space between children
          })}
        >
          <TextField
            type="text"
            value={groupName}
            label="Group Name"
            onChange={(e) => setGroupName(e.target.value)}
          />
          <Button
            disabled={!groupName}
            loading={createGroupLoading}
            onClick={async () => {
              await createGroup({ name: groupName });
              setModalState(undefined);
              setGroupName('');
              await refetchGroups();
            }}
          >
            Submit
          </Button>
        </Grid>
      </Modal>
    </Grid>
  )
}